import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEndPoints, ApiRoot } from '../../shared/constants/api-endpoints';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class VenueTokenService {
  private readonly storageKey = 'venue_service_token';
  private inFlight$?: Observable<string>;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  ensureVenueToken(): Observable<string> {
    const cached = this.getValidCachedToken();
    if (cached) {
      return of(cached);
    }

    if (!this.inFlight$) {
      this.inFlight$ = this.requestNewToken().pipe(
        shareReplay(1),
        finalize(() => {
          this.inFlight$ = undefined;
        })
      );
    }

    return this.inFlight$;
  }

  private getValidCachedToken(): string | null {
    const token = String(localStorage.getItem(this.storageKey) ?? '').trim();
    if (!token) {
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    const exp = Number(payload?.['exp'] ?? 0);
    if (!exp) {
      return null;
    }

    const bufferMs = 60_000;
    return exp * 1000 - bufferMs > Date.now() ? token : null;
  }

  private requestNewToken(): Observable<string> {
    const accessToken = String(this.authService.getAccessToken() ?? '').trim();
    if (!accessToken) {
      return throwError(() => new Error('Access token is required to fetch venue token'));
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
    });

    const venueTokenUrl = `${environment.api_gateway}${ApiRoot.LIFERAY_TOKEN_REST}/${ApiEndPoints.LIFERAY_TOKEN}`;

    return this.http.post<any>(venueTokenUrl,"", { headers }).pipe(
      map((res: any) => {
        const token = String(res?.access_token ?? '').trim();
        if (!token) {
          throw new Error('Venue token response missing access_token');
        }
        this.persistToken(token);
        return token;
      }),
      catchError((error) => {
        console.error('Failed to fetch venue token', error);
        return throwError(() => error);
      })
    );
  }

  private persistToken(token: string): void {
    if (!token) {
      return;
    }
    localStorage.setItem(this.storageKey, token);
  }

  private decodeJwtPayload(token: string): Record<string, any> | null {
    const parts = String(token ?? '').split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const normalized = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
      return JSON.parse(window.atob(padded));
    } catch {
      return null;
    }
  }
}
